import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api, API_URL } from '../../services/api';
import { tokens as t } from '../../styles/appTheme';
import MiniMarkdown from './MiniMarkdown';

export const EXAMPLE_QUESTIONS = [
  'Is candy taxable in Illinois?',
  'Sales tax rate for 80302',
  'Are diapers taxed in Texas?',
  'What changes are coming in Colorado?',
];

interface Source { title: string; url: string }
interface Msg { role: 'user' | 'assistant'; content: string; sources?: Source[]; asOf?: string }
interface CompanyOption { id: number; name: string }

const GENERIC_ERROR = "I couldn't answer that just now. Please try again in a moment.";

/** The Tax Advisor conversation: examples, input, answers with sources and the as-of date. */
const AdvisorChat: React.FC<{ compact?: boolean; autoFocus?: boolean; examples?: string[]; page?: string }> = ({ compact = false, autoFocus = false, examples = EXAMPLE_QUESTIONS, page }) => {
  const { isAuthenticated, user } = useAuth() as any;
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState<string | null>(null);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companiesHidden, setCompaniesHidden] = useState(0);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [defaultCompanyId, setDefaultCompanyId] = useState<number | null>(null);
  const previousCompanyId = useRef<number | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/v1/advisor/my-context`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.ok ? r.json() : null).then((data) => {
        if (!data?.requires_company_selection) return;
        const options = data.companies || []; setCompanies(options); setCompaniesHidden(Number(data.companies_hidden || 0)); setCompanyId(data.default_company_id ?? options[0]?.id ?? null); previousCompanyId.current = data.default_company_id ?? options[0]?.id ?? null; setDefaultCompanyId(data.default_company_id ?? options[0]?.id ?? null);
      }).catch(() => undefined);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!user?.is_app_admin) return;
    api.get('/api/v1/advisor/admin-status')
      .then((r: any) => setAdminNote(r.data?.ai_enabled ? null : r.data?.note || null))
      .catch(() => undefined);
  }, [user?.is_app_admin]);

  useEffect(() => {
    if (messages.length) endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, busy]);

  const ask = useCallback(async (q: string) => {
    const question = q.trim();
    if (!question || busy) return;
    const switchedCompany = companyId !== null && previousCompanyId.current !== null && companyId !== previousCompanyId.current;
    const history = (switchedCompany ? [] : messages.slice(-6)).map((m) => ({ role: m.role, content: m.content.slice(0, 1800) }));
    previousCompanyId.current = companyId;
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setInput('');
    setBusy(true);
    setLimit(null);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/v1/advisor/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ question, history, ...(page ? { page } : {}), ...(companyId ? { company_id: companyId } : {}) }),
      });
      if (resp.status === 429) {
        const body = await resp.json().catch(() => ({}));
        setLimit(body?.detail?.message || 'You have asked a lot of questions in a short time. Please wait a bit.');
        setMessages((m) => m.slice(0, -1));
        setInput(question);
        return;
      }
      if (!resp.ok) throw new Error(String(resp.status));
      const data = await resp.json();
      setMessages((m) => [...m, { role: 'assistant', content: data.answer || GENERIC_ERROR, sources: data.sources, asOf: data.as_of }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: GENERIC_ERROR }]);
    } finally {
      setBusy(false);
    }
  }, [busy, messages, companyId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {companies.length > 1 && <TextField select size="small" label="Company for this conversation" value={companyId ?? ''} onChange={(event) => setCompanyId(Number(event.target.value))} sx={{ mb: companiesHidden > 0 ? 0.5 : 2, maxWidth: 360 }}>{companies.map((company) => <MenuItem key={company.id} value={company.id}>{company.name}{company.id === defaultCompanyId ? ' (default)' : ''}</MenuItem>)}</TextField>}
      {companiesHidden > 0 && <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>Showing your default company and {companies.length - 1} more. Use the company selector on the page to switch to the other {companiesHidden}.</Typography>}
      {adminNote && <Alert severity="info" sx={{ mb: 2 }}>Admin note: {adminNote}</Alert>}

      {messages.length > 0 && (
        <Stack spacing={2} aria-live="polite" sx={{ mb: 2, maxHeight: compact ? 360 : 'none', overflowY: compact ? 'auto' : 'visible' }}>
          {messages.map((m, i) => (
            m.role === 'user' ? (
              <Box key={i} data-testid="advisor-user-msg" sx={{ alignSelf: 'flex-end', maxWidth: '85%', bgcolor: t.accent, color: t.accentInk, px: 2, py: 1.25, borderRadius: `${t.radiusCard}px` }}>
                <Typography variant="body1">{m.content}</Typography>
              </Box>
            ) : (
              <Box key={i} data-testid="advisor-answer" sx={{ alignSelf: 'flex-start', maxWidth: '100%', bgcolor: t.paper2, border: `1px solid ${t.rule}`, px: 2.25, py: 1.75, borderRadius: `${t.radiusCard}px` }}>
                <MiniMarkdown text={m.content} />
                {!!m.sources?.length && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.25 }}>
                    Source:{' '}
                    {m.sources.slice(0, 3).map((s, j) => (
                      <React.Fragment key={s.url}>{j > 0 && ', '}<Link href={s.url} target="_blank" rel="noopener noreferrer">{s.title}</Link></React.Fragment>
                    ))}
                  </Typography>
                )}
              </Box>
            )
          ))}
          {busy && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Checking our rate tables and rules...</Typography>
            </Stack>
          )}
          <div ref={endRef} />
        </Stack>
      )}

      {limit && (
        <Alert severity="warning" sx={{ mb: 2 }} action={!isAuthenticated ? <Button component={RouterLink} to="/get-started" size="small" color="inherit">Create free account</Button> : undefined}>
          {limit}
        </Alert>
      )}

      <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); ask(input); }} sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          autoFocus={autoFocus}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a sales tax question, like “Are hoodies taxed in New York?”"
          inputProps={{ 'aria-label': 'Ask a sales tax question', maxLength: 600, 'data-testid': 'advisor-input' }}
          disabled={busy}
          sx={{ bgcolor: t.paper2 }}
        />
        <IconButton type="submit" color="primary" aria-label="Send question" data-testid="advisor-send" disabled={busy || !input.trim()}
          sx={{ bgcolor: t.accent, color: t.accentInk, borderRadius: `${t.radiusControl}px`, width: 52, '&:hover': { bgcolor: t.accentDeep }, '&.Mui-disabled': { bgcolor: t.paper3, color: t.muted } }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
        {examples.map((q) => (
          <Chip key={q} data-testid="advisor-example" label={q} variant="outlined" onClick={() => ask(q)} disabled={busy} clickable sx={{ bgcolor: t.paper2 }} />
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
        General information based on current state rules, not legal or tax advice. Confirm important decisions with the state or a tax professional.
      </Typography>
    </Box>
  );
};

export default AdvisorChat;
